import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  GAME_LAUNCH_EVENT, GAME_MINI_PLAYER_EVENT, GAME_PLAYER_ACTIVITY_EVENT,
  GAME_PLAYER_FRAME_LOADED_EVENT, readStoredGameLaunch, readStoredMiniGamePlayer,
  isGameLaunchLockedByAnotherWindow, startGameLaunchHeartbeat, finishGameSession,
} from '../../../lib/gameLaunch';
import useGameFrameSlot from '../hook/useGameFrameSlot.hook';
import { markGameFrameLoaded } from '../state/frameLoad.state';
import { bindMobileGameSession } from '../lifecycle/gamePlayerSession.lifecycle';
import MobileGameControls from './MobileGameControls.component';
import { bindGamePopupPresentation, reconcileGamePopup } from '../lifecycle/mobilePopout.lifecycle';
import { getSafeGameReturnPath, isSameGameLaunchSnapshot } from '../policy/launchUrl.policy';

// One iframe and one heartbeat survive full/mini/theater presentation changes.
// The fixed body portal target must never depend on a route or slot element.
export default function PersistentGameFrame({ accountKey }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const elementRef = useRef(null);
  const mobileLifecycleRef = useRef(null);
  const [mobile, setMobile] = useState(() => window.innerWidth < 1024);
  const [external, setExternal] = useState(() => document.body.classList.contains('crypto-game-player-popout'));
  const [manualCloseNotice, setManualCloseNotice] = useState(null);
  const popoutRef = useRef(null);
  const [launch, setLaunch] = useState(readStoredGameLaunch);
  const [mini, setMini] = useState(readStoredMiniGamePlayer);
  const [conflict, setConflict] = useState(isGameLaunchLockedByAnotherWindow);
  const wasActive = useRef(false);
  const accountRef = useRef(accountKey);
  const previousPath = useRef(pathname);
  const authenticated = typeof accountKey === 'string' && accountKey.length > 0;
  const sameAccount = accountRef.current === undefined || accountRef.current === accountKey;
  const mode = pathname === '/play' ? 'full' : 'mini';
  const active = Boolean(authenticated && sameAccount && launch?.playUrl && !conflict && (mode === 'full' || mini?.active));
  const rect = useGameFrameSlot(mode, active);
  const sessionRef = useRef(null);
  sessionRef.current = { active, launch, pathname, navigate };
  const manualCloseIntent = manualCloseNotice?.accountKey === accountKey &&
    isSameGameLaunchSnapshot(manualCloseNotice?.launch, launch) ? manualCloseNotice?.intent : null;

  useEffect(() => { setManualCloseNotice(null); },
    [active, external, mobile, accountKey, launch?.openedAt, launch?.playUrl, launch?.kind, launch?.gameId, launch?.lobbyId]);

  useEffect(() => {
    const resize = () => setMobile(window.innerWidth < 1024);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useLayoutEffect(() => {
    if (!active || !mobile || external || !elementRef.current) return undefined;
    const lifecycle = bindMobileGameSession(elementRef.current);
    mobileLifecycleRef.current = lifecycle;
    void lifecycle.requestFullscreen();
    return () => { lifecycle.cleanup(); mobileLifecycleRef.current = null; };
  }, [active, mobile, external]);

  useEffect(() => {
    const controller = bindGamePopupPresentation({ manualClose: true, setExternal: (value) => {
      document.body.classList.toggle('crypto-game-player-popout', value);
      flushSync(() => setExternal(value));
      window.dispatchEvent(new CustomEvent(GAME_PLAYER_ACTIVITY_EVENT));
    }, canOpen: (url, expected) => Boolean(sessionRef.current?.active && url && url === expected?.playUrl && isSameGameLaunchSnapshot(expected, readStoredGameLaunch())),
    onClosed: () => {
      const current = sessionRef.current;
      const returnPath = getSafeGameReturnPath(current?.launch?.sourcePath);
      finishGameSession('game-popup-closed');
      if (current?.pathname === '/play') current.navigate(returnPath, { replace: true });
    } });
    popoutRef.current = controller;
    return () => { controller.cleanup(); popoutRef.current = null; };
  }, []);

  useEffect(() => {
    if (!active) popoutRef.current?.close();
  }, [active]);

  useEffect(() => {
    const sync = () => {
      setLaunch(readStoredGameLaunch());
      setMini(readStoredMiniGamePlayer());
      setConflict(isGameLaunchLockedByAnotherWindow());
      setExternal(document.body.classList.contains('crypto-game-player-popout'));
    };
    const events = [GAME_LAUNCH_EVENT, GAME_MINI_PLAYER_EVENT, GAME_PLAYER_ACTIVITY_EVENT, 'storage'];
    events.forEach((event) => window.addEventListener(event, sync));
    return () => events.forEach((event) => window.removeEventListener(event, sync));
  }, []);

  useLayoutEffect(() => {
    if (accountKey === undefined) return;
    if (accountKey === null || (accountRef.current !== undefined && accountRef.current !== accountKey)) {
      finishGameSession('game-player-account-change', { refresh: false });
      setLaunch(null);
    }
    accountRef.current = accountKey;
  }, [accountKey]);

  useLayoutEffect(() => {
    if (previousPath.current !== pathname && pathname !== '/play' && !mini?.active) {
      finishGameSession('game-player-route-exit');
    }
    previousPath.current = pathname;
  }, [pathname, mini?.active]);

  useLayoutEffect(() => {
    document.body.classList.toggle('crypto-game-player-active', active && !external);
    window.dispatchEvent(new CustomEvent(GAME_PLAYER_ACTIVITY_EVENT));
    if (wasActive.current && !active && launch?.playUrl && !mini?.active) {
      finishGameSession('game-player-route-exit');
    }
    wasActive.current = active;
    return () => document.body.classList.remove('crypto-game-player-active');
  }, [active, launch?.playUrl, mini?.active, external]);

  useEffect(() => {
    if (!active) return undefined;
    const heartbeat = startGameLaunchHeartbeat(launch, { releaseOnCleanup: false, onHeartbeat: reconcileGamePopup });
    if (!heartbeat.success) setConflict(true);
    return heartbeat.cleanup;
  }, [launch, active]);

  if (!authenticated || !sameAccount || !launch?.playUrl || conflict || (!active && !wasActive.current)) return null;
  const exit = () => {
    if (external) {
      if (popoutRef.current?.requestManualClose({ restoreInline: false })) {
        setManualCloseNotice({ intent: 'exit', accountKey, launch });
      }
      return;
    }
    const path = getSafeGameReturnPath(launch.sourcePath);
    finishGameSession('game-player-exit');
    navigate(path, { replace: true });
  };
  return createPortal(
    <section ref={elementRef} data-persistent-game-root="true" tabIndex={-1} aria-label={mobile ? '게임 플레이어' : undefined}
      role={mobile ? 'dialog' : undefined} aria-modal={mobile || undefined}
      style={{ position: 'fixed', background: '#000', overflow: 'hidden',
        ...(mobile ? { inset: 0, width: '100%', height: '100dvh', display: 'flex', flexDirection: 'column',
          paddingTop: 'env(safe-area-inset-top, 0px)', boxSizing: 'border-box' } : rect),
        zIndex: mobile ? 2147483000 : mode === 'mini' ? 191 : 1,
        visibility: active && (mobile || (!external && rect)) ? 'visible' : 'hidden', pointerEvents: active ? 'auto' : 'none' }}>
    {!external ? <iframe
      data-persistent-game-frame="true"
      allow="fullscreen; autoplay; clipboard-read; clipboard-write; encrypted-media; payment"
      allowFullScreen
      referrerPolicy="no-referrer-when-downgrade"
      src={launch.playUrl}
      title={`${launch.label || '게임'} ${mode === 'mini' ? '미니플레이어 화면' : '실행 화면'}`}
      onLoad={() => {
        markGameFrameLoaded(launch);
        window.dispatchEvent(new CustomEvent(GAME_PLAYER_FRAME_LOADED_EVENT));
      }}
      style={{ display: 'block', border: 0, background: '#000', width: '100%', height: '100%', minHeight: 0, flex: '1 1 0' }}
    /> : mobile && <div role="status" style={{ flex: 1, display: 'grid', placeContent: 'center', color: '#fff', fontSize: 13 }}>
      {manualCloseIntent === 'return' ? '게임 새창을 직접 닫아주세요. 닫힘 확인 후 내부 화면으로 돌아옵니다.'
        : manualCloseIntent === 'exit' ? '게임 새창을 직접 닫아주세요. 닫힘 확인 후 게임이 종료됩니다.'
          : '새창에서 게임 중입니다. 복귀·종료 선택 후 새창을 직접 닫아주세요.'}
    </div>}
    {mobile && <MobileGameControls onFullscreen={() => mobileLifecycleRef.current?.requestFullscreen()} onExit={exit} external={external}
      onExternal={() => {
        if (external) {
          if (popoutRef.current?.returnInline()) setManualCloseNotice({ intent: 'return', accountKey, launch });
          return;
        }
        const ok = popoutRef.current?.open(launch.playUrl, undefined, launch);
        if (!ok) window.alert('새창을 열 수 없습니다. 팝업 허용 설정을 확인해주세요.');
      }} />}
    </section>,
    document.body
  );
}
